import React, { useState } from "react";
import { TextInput } from "../components/FormInputs";
import { DAY_THEMES, CYCLE_PHASES } from "../constants/cycleLegend";
import "../styles/buttons.css";
import "./AdminEventForm.css";

/**
 * AdminEventForm — demo of the "someone has to type this in" half of
 * Option A's CRUD. Posts to POST /api/admin/events (or PUT
 * /api/admin/events/:id when editing) — this was missing the /api prefix
 * originally, which meant it never actually reached any route handler
 * (everything in this app mounts under /api). Reuses the same TextInput
 * primitive as Register/Login, so it stays visually consistent with the
 * rest of the app rather than needing a separate "admin theme."
 *
 * Props:
 *  - initialEvent: existing event to edit (omit for create mode)
 *  - onSaved: called with the saved event after a successful submit
 */
export default function AdminEventForm({ initialEvent, onSaved }) {
  const isEditing = Boolean(initialEvent?.id);
  const [form, setForm] = useState({
    title: initialEvent?.title || "",
    startsAt: initialEvent?.startsAt || "",
    location: initialEvent?.location || "",
    description: initialEvent?.description || "",
    capacity: initialEvent?.capacity || "",
    rsvpEnabled: initialEvent?.rsvpEnabled || false,
    recurrence: initialEvent?.recurrence || "none",
    dayOfWeek: initialEvent?.dayOfWeek ?? "",
    imagepath: initialEvent?.imagepath || "",
    link: initialEvent?.link || "",
  });
  const [status, setStatus] = useState("idle"); // idle | saving | error

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("saving");
    try {
      const url = isEditing ? `/api/admin/events/${initialEvent.id}` : "/api/admin/events";
      const res = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          capacity: form.capacity ? Number(form.capacity) : null,
          dayOfWeek: form.recurrence === "weekly" && form.dayOfWeek !== "" ? Number(form.dayOfWeek) : null,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      const saved = await res.json();
      setStatus("idle");
      onSaved && onSaved(saved);
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  }

  return (
    <div className="admin-event-layout">
      <form className="admin-event-form" onSubmit={handleSubmit}>
      <TextInput
        label="Title"
        value={form.title}
        onChange={(e) => update("title", e.target.value)}
        required
      />

      <div className="admin-event-form-recurrence">
        <label>
          <input
            type="radio"
            name="recurrence"
            checked={form.recurrence === "none"}
            onChange={() => update("recurrence", "none")}
          />
          One-time
        </label>
        <label>
          <input
            type="radio"
            name="recurrence"
            checked={form.recurrence === "weekly"}
            onChange={() => update("recurrence", "weekly")}
          />
          Weekly
        </label>
      </div>

      {form.recurrence === "weekly" ? (
        <div className="admin-event-form-field">
          <label>Day of week</label>
          <select value={form.dayOfWeek} onChange={(e) => update("dayOfWeek", e.target.value)} required>
            <option value="" disabled>Select a day…</option>
            {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day, i) => (
              <option key={day} value={i}>{day}</option>
            ))}
          </select>
        </div>
      ) : null}

      <TextInput
        label={form.recurrence === "weekly" ? "Time (any reference date)" : "Date & time"}
        type="datetime-local"
        value={form.startsAt}
        onChange={(e) => update("startsAt", e.target.value)}
        required
      />
      <TextInput
        label="Location"
        value={form.location}
        onChange={(e) => update("location", e.target.value)}
      />
      <TextInput
        label="Photo URL (optional)"
        value={form.imagepath}
        onChange={(e) => update("imagepath", e.target.value)}
        placeholder="https://..."
      />
      <TextInput
        label="Ticket link (optional)"
        value={form.link}
        onChange={(e) => update("link", e.target.value)}
        placeholder="https://..."
      />
      <div className="admin-event-form-field">
        <label>Description (optional)</label>
        <textarea
          rows={3}
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
        />
      </div>
      <TextInput
        label="Capacity (optional)"
        type="number"
        value={form.capacity}
        onChange={(e) => update("capacity", e.target.value)}
      />
      <label className="admin-event-form-checkbox">
        <input
          type="checkbox"
          checked={form.rsvpEnabled}
          onChange={(e) => update("rsvpEnabled", e.target.checked)}
        />
        Enable RSVP
      </label>

      <button type="submit" className="mihu-btn-primary-lg" disabled={status === "saving"}>
        {status === "saving" ? "Saving…" : isEditing ? "Save changes" : "Create event"}
      </button>
      {status === "error" && (
        <div className="admin-event-form-error">Couldn't save — try again.</div>
      )}
      </form>

      <div className="admin-event-legend">
        <div className="admin-event-legend-section">
          <div className="admin-event-legend-title">Daily focus</div>
          <div className="admin-event-legend-days">
            {DAY_THEMES.map((d) => (
              <div
                className="admin-event-legend-day"
                key={d.label}
                style={{ borderLeftColor: d.color }}
              >
                <span className="admin-event-legend-day-name">{d.label}</span>
                <span className="admin-event-legend-day-theme" style={{ color: d.color }}>
                  {d.theme}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-event-legend-section">
          <div className="admin-event-legend-title">Weekly focus — 28-day cycle</div>
          <div className="admin-event-legend-phases">
            {CYCLE_PHASES.map((p) => (
              <div
                className="admin-event-legend-phase"
                key={p.phase}
                style={{ borderLeftColor: p.color }}
              >
                <div className="admin-event-legend-phase-head">
                  <span
                    className="admin-event-legend-phase-num"
                    style={{ background: p.color }}
                  >
                    {p.phase}
                  </span>
                  <span className="admin-event-legend-phase-name">{p.name}</span>
                  <span className="admin-event-legend-phase-duration">{p.duration}</span>
                </div>
                <div className="admin-event-legend-phase-activities">
                  {p.activities.join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
